<template>
  <section id="about" class="about-page container">
    <div class="about-shell">
      <div class="about-header">
        <p class="about-kicker">About The Platform</p>
        <h1 class="about-title">A central digital inventory for health coordination in Nigeria</h1>
      </div>

      <div class="about-copy">
        <p>
          This central web-based inventory has been developed by the Department of Health Planning,
          Research and Statistics (DHPRS) of the Federal Ministry of Health (FMoH) to compile all
          health data and intervention projects carried out by international partners and
          non-governmental organisations across all health domains in the country. The platform also
          captures details of Non Governmental Organizations (NGOs) that have been registered with
          the Federal Ministry of Health under the purview of the NGO branch at the Health Systems
          Strengthening division of the DHPRS-FMoH.
        </p>
        <p>
          This information will be used to enhance coordination of all health data activities and
          projects, and increase visibility of these activities and resources to the general public.
          This website contains inventories of all health related data activities and health partner
          projects in the country.
        </p>
        <p>
          Section 3.2 (Page 9) of the 2014 National HIS Policy states that all stakeholders,
          including bilateral and multilateral agencies and other international development
          agencies/programmes working at national and subnational levels, shall support HIS
          activities through existing government structures.
        </p>
      </div>

      <div class="about-note">
        <p>
          A bilateral organization is a government agency or non-profit organization based in a
          single country that provides aid to people in other countries.
        </p>
        <p>
          A multilateral organization is formed by three or more nations to work on issues that are
          relevant to each of them.
        </p>
      </div>

      <div class="about-copy">
        <p>
          Performance of programmes and projects supported by international development
          organizations shall be assessed using data obtainable from the approved national data
          collection system.
        </p>
        <p class="about-intro">This inventory has been divided into four sections.</p>
      </div>

      <div class="about-grid">
        <article class="about-card">
          <h2>The Health Data Inventory</h2>
          <p>
            An inventory of all health related data activities, datasets, and data portfolios in the
            country.
          </p>
          <router-link to="/inventory/dataset">Explore Data Inventory</router-link>
        </article>

        <article class="about-card">
          <h2>The Health Projects Inventory</h2>
          <p>
            An inventory of health partners and their projects being implemented across the country.
          </p>
          <router-link to="/inventory/project">Explore Projects Inventory</router-link>
        </article>

        <article class="about-card">
          <h2>The NGO Section</h2>
          <p>
            An inventory of registered Nigerian NGOs, their contact information, and their activity
            reports.
          </p>
          <router-link to="/ngo/landing">Explore NGO Inventory</router-link>
        </article>

        <article class="about-card">
          <h2>The Stakeholder Database</h2>
          <p>
            A comprehensive record of stakeholders' information and areas of support to the DHPRS.
          </p>
          <router-link to="/inventory/stakeholders">Explore Stakeholder Inventory</router-link>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'about',
};
</script>

<style scoped>
.about-page {
  padding-top: clamp(4.5rem, 6vw, 5.75rem);
  padding-bottom: 4rem;
}

.about-shell {
  position: relative;
  padding: clamp(1.5rem, 3vw, 2.5rem);
  border-radius: 32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(244, 249, 245, 0.95));
  border: 1px solid rgba(28, 69, 44, 0.08);
  box-shadow: 0 24px 60px rgba(17, 41, 24, 0.08);
  overflow: hidden;
}

.about-shell::before {
  content: '';
  position: absolute;
  top: -2rem;
  right: -2rem;
  width: 16rem;
  height: 16rem;
  background: radial-gradient(circle, rgba(48, 171, 76, 0.14), transparent 70%);
  pointer-events: none;
}

.about-header,
.about-copy,
.about-note,
.about-grid {
  position: relative;
  z-index: 1;
}

.about-header {
  max-width: 54rem;
  margin-bottom: 1.75rem;
}

.about-kicker {
  margin: 0 0 0.55rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #2a8a43;
}

.about-title {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.25rem);
  line-height: 1.08;
  font-weight: 700;
  color: #153322;
}

.about-copy {
  display: grid;
  gap: 1rem;
}

.about-copy p,
.about-note p,
.about-card p {
  margin: 0;
  font-family: 'Roboto', sans-serif;
  font-size: 1.02rem;
  line-height: 1.8;
  color: #4f6759;
}

.about-note {
  display: grid;
  gap: 0.85rem;
  margin: 1.5rem 0;
  padding: 1.2rem 1.25rem;
  border-radius: 24px;
  background: rgba(29, 111, 51, 0.05);
  border: 1px solid rgba(29, 111, 51, 0.08);
}

.about-intro {
  font-weight: 700;
  color: #183523;
}

.about-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.75rem;
}

.about-card {
  display: grid;
  gap: 0.8rem;
  padding: 1.35rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(28, 69, 44, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.about-card h2 {
  margin: 0;
  font-size: 1.1rem;
  line-height: 1.35;
  font-weight: 700;
  color: #183523;
}

.about-card a {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.75rem 0.95rem;
  border-radius: 999px;
  background: rgba(47, 159, 73, 0.1);
  color: #1f6f33;
  font-weight: 700;
  text-decoration: none;
}

@media (max-width: 991px) {
  .about-page {
    padding-top: 4.5rem;
  }

  .about-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .about-page {
    padding-top: 4rem;
    padding-bottom: 2.5rem;
  }

  .about-shell {
    padding: 1.25rem;
    border-radius: 24px;
  }

  .about-header {
    margin-bottom: 1.25rem;
  }

  .about-title {
    font-size: 1.7rem;
    line-height: 1.12;
  }

  .about-kicker {
    font-size: 0.7rem;
    letter-spacing: 0.14em;
  }

  .about-copy p,
  .about-note p,
  .about-card p {
    font-size: 0.95rem;
    line-height: 1.7;
  }

  .about-card {
    padding: 1.1rem;
    border-radius: 20px;
  }
}

@media (max-width: 575px) {
  .about-page {
    padding-top: 3.5rem;
    padding-bottom: 2rem;
  }

  .about-shell {
    padding: 1rem;
    border-radius: 20px;
  }

  .about-header {
    margin-bottom: 1rem;
  }

  .about-title {
    font-size: 1.45rem;
  }

  .about-copy {
    gap: 0.85rem;
  }

  .about-copy p,
  .about-note p,
  .about-card p {
    font-size: 0.9rem;
    line-height: 1.65;
  }

  .about-note {
    margin: 1rem 0 1.25rem;
    padding: 1rem;
    border-radius: 18px;
  }

  .about-grid {
    gap: 0.85rem;
    margin-top: 1.25rem;
  }

  .about-card {
    gap: 0.7rem;
    padding: 1rem;
    border-radius: 18px;
  }

  .about-card h2 {
    font-size: 1rem;
  }

  .about-card a {
    width: 100%;
    justify-content: center;
    text-align: center;
    padding: 0.8rem 0.9rem;
  }
}
</style>
